/**
 * This module contains common types which are reused by AppFrame Services
 *
 * @module Common Types
 */

import { FC, MouseEvent } from 'react';

/**
 * Defines a rule / ruleset to determinate if a feature is available.
 *
 * See {@link WithAvailable} interface for more information how to interpret availability rules.
 */
export type AvailabilityRule = boolean | string | string[] | string[][];

/**
 * Defines a property to control the availability of an module, item or feature
 *
 * Availability check is used to determine if the item should be displayed or not regarding to
 * available user rights or feature flags.
 *
 * The available flag can have a lot of different formats:
 * - it can be a single boolean value like `true` or `false` value, to module is statically
 *   displayed or hidden.
 * - it can be a simple string, which will be parsed as an OIDC right. The icon is displayed only,
 *   when OIDC user profile contains the specified right.
 * - it can be a variable string in format `${variable.name.as.path}` which can refer to OIDC
 *   rights / audiences availability or the availability of some configuration variable:
 *   - OIDC right: a string `"${right.MyAppRight}"` will display the menu item on sidebar, when
 *     the OIDC user profile contains `MyAppRight` right
 *   - OIDC audience: a string `"${audience.MyAppClient}"` will display the menu item when
 *     `MyAppClient` is mapped into `aud` in OIDC `access_token`
 *   - module feature: when a module has a `featureUrl` prop set in its module configuration,
 *     the URL response data can be referred with a `${features.myModule.myFeature}` string. Only
 *     booly types are supported, so a value is considered to be `false` when its value is
 *     `false`, `0`, `null`, `undefined` or an empty string, otherwise it is considered to be
 *     `true`.
 *   - configuration property a kind of global feature flag:
 *     - a string `"${configs.main.myAppFrameFeature}"` refers to the existance of a bool/booly
 *       prop `myAppFrameFeature` in `server-config.json`
 *     - a string `"${configs.myApp.myAppFeature}"` refers to the existance of a bool/booly prop
 *       `myAppFeature` in `myApp` application which is loaded by application configuration
 *       loading mechanism.
 * - it can be an array (or array of array) to define logical AND / OR expressions:
 *   - item types like boolean and string are parsed as they are described above and are connected
 *     eachother with logical OR operation: this mean if any of the items are allowing display the
 *     item will be displayed;
 *   - also an item can be another array which also can contain boolean and string items. These
 *     items are parsed on the same way as described above, and they are connected eachother with
 *     logical AND operation: this means that module is displayed only when all the rules in the
 *     array are allowing the item to display.
 *
 * @example
 * The following examples shows how the `available` property can be set in a module configuration
 * or other places where the `WithAvailable` interface is used:
 * - always available:
 *   ```json
 *   "available": true
 *   ```
 * - never available:
 *   ```json
 *   "available": false
 *   ```
 * - available when RightA || RightB rights are granted:
 *   ```json
 *   "available": ["RightA", "RightB"]
 *   ```
 * - available when AppFrame's appFrameFeature feature flag && myApp's myAppFeature feature
 *   flag && RightA) || (RightA && RightB) || RightC is set:
 *   ```json
 *   "available": [
 *     ["${configs.main.myAppFrameFeature}", "${configs.myApp.myAppFeature}", "RightA"],
 *     ["RightA", "RightB"],
 *     "RightC"
 *   ]
 * ```
 */
export interface WithAvailable {
  /**
   * The availability rule wether to display or not to display the menu item.
   */
  available?: AvailabilityRule;
}

/**
 * A method which is usually returned by subscription services (eg. onWidgetChangeService).
 * Invoking the method will unsubscribe from the actual event.
 */
export type UnsubscribeMethod = () => void;

/**
 * Indicates the type of the modules.
 */
export enum ModuleType {
  /**
   * Application modules are modules which repesents an single module which can displayed in the main view.
   *
   * Application modules has the following features:
   *  - application modules must export Single SPA parcel lifecycle methods:
   *    `bootstrap`, `mount`, `unmount` and optionally `update`
   *  - application modules are not loaded on startup (on-demand loading)
   *  - in most cases a sidebar / mobile navigation application item belongs to them
   *  - when the sidebar item is clicked the module is loaded and displayed in the main view
   */
  Application = 'application',
  /**
   * Module is a plugin type module.
   *
   * Plugin type modules are loaded when AppFrame Starts and can have the following async lifecycle methods:
   *  - `prelogin`: async method invoked before any authentication happens
   *    (optional, called only when `prelogin` feature is enabled)
   *  - `preboot`: async method invoked after authentication - it is used to register module specific
   *    features (filters, actions services and widgets)
   *  - `boot`: async method invoked before AppFrame renders the first module - it can be used for any
   *    generic purpose, functionalites
   */
  Plugin = 'plugin',
}

/**
 * Defines the route type of a sidebar or mobile navigation application item
 */
export enum RouteType {
  /**
   * The route of the sidebar module must be handled as an application route.
   * AppFrame supports only hash routes now.
   * This routes are handled in AppFrame by `react-router-dom` and application modules
   * must handle hash route changes by their own.
   */
  Route = 'route',
  /**
   * The route of the sidebar module is a URL which handled by the browser.
   * This means `target`, `noopener`and `noreferrer` props also handled.
   */
  Url = 'url',
}

/**
 * The navigation area where the an application module icons can be displayed.
 * This parameter is often handled by configurations whether to display an
 * application module icon on mobile, desktop or both platforms.
 */
export enum NavigationArea {
  /**
   * The application module icon is displayed on the desktop sidebar
   */
  Sidebar = 'sidebar',
  /**
   * The application module icon is displayed on the mobile navigation menu
   * 'Applications' section.
   */
  Mobile = 'mobile',
}

/**
 * Defines a property to control navigation area visibility of a menu item.
 */
export interface WithNavigationArea {
  /**
   * The navigation location (mobile or desktop) where to display the menu item.
   */
  navigation?: NavigationArea | NavigationArea[];
}

/**
 * The basic type definition of a component which is able to close the menu which was
 * triggered it.
 */
export type CloseableComponentProps = {
  /**
   * Callback method which can be invoked to close the parent menu.
   */
  onMenuClose?: (event?: MouseEvent<HTMLElement>) => void;
};

/**
 * Defines a React Functional Component with an `onMenuClose` callback function which can be
 * used to close the parent menu.
 */
export type CloseableComponent = FC<CloseableComponentProps> & {
  defaultProps?: object;
  propTypes?: object;
};

/**
 * Enum to select the log level.
 */
export enum LogLevel {
  /**
   * In debug log level every information is displayed.
   */
  DEBUG = 'DEBUG',
  /**
   * Informational, warning and error messages are displayed.
   */
  INFO = 'INFO',
  /**
   * Warning and error messages are displayed.
   */
  WARNING = 'WARNING',
  /**
   * Only error messages are displayed.
   */
  ERROR = 'ERROR',
}

/**
 * Enum to select the Single App Mode behavior.
 */
export enum SingleAppMode {
  /**
   * AppFrame will rendered in Single App Mode only when there are multiple items in the sidebar navigation.
   */
  Auto = 'auto',
  /**
   * AppFrame will always rendered in Single App Mode independently from the number of sidebar menu items.
   */
  Always = 'always',
}
